import type { ComponentType } from 'react'
import { cn } from '../lib/utils'

/**
 * RadioCardGroup — выбор ОДНОГО значения в форме крупными карточками в сетке.
 * Третий паттерн выбора (после сегмент-контрола и чипа): когда вариантов много,
 * у каждого иконка/описание, и они не помещаются в дорожку или ряд чипов.
 * Карточки лежат на СПЛОШНОМ bg-card, поэтому все состояния — по сплошной лестнице
 * elevation (белый оверлей тут сделал бы ТЕМНЕЕ, а не светлее):
 * покой bg-card (14%) → ховер bg-popover (18%) → нажатие active:bg-muted (23%) →
 * ВЫБРАНО bg-muted (23%, самый светлый) + border-foreground/40. Бренд не тратится.
 */
export interface RadioCardOption {
  id: string
  label: string
  /** Иконка lucide или своя (цветная): принимает класс размера и толщину штриха. */
  icon?: ComponentType<{ className?: string; strokeWidth?: string | number }>
  /** CSS-значение `background` для образца-квадратика перед подписью. Нужен там,
   *  где выбирается сам цвет/фон и подписи недостаточно (выбор темы блока). */
  swatch?: string
  /** Погасить ОДНУ карточку (выбирать нельзя), не всю группу. */
  disabled?: boolean
}

interface RadioCardGroupProps {
  options: RadioCardOption[]
  value: string | null
  onChange: (id: string) => void
  columns?: 1 | 2 | 3
  className?: string
  /**
   * Блокировка настоящая: native disabled на кнопках + aria-disabled на группе.
   * Гасить снаружи одним pointer-events-none нельзя — Tab+Space остаётся рабочим.
   * Визуал в состоянии покоя байт-в-байт как у близнеца <CheckCardGroup>.
   */
  disabled?: boolean
  /** Доступное имя группы. Без него role="radiogroup" безымянна, и группу
   *  нельзя ни найти в тесте, ни озвучить скринридером. */
  ariaLabel?: string
}

const colsClass: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
}

export function RadioCardGroup({
  options,
  value,
  onChange,
  columns = 2,
  className,
  disabled = false,
  ariaLabel,
}: RadioCardGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={cn('grid gap-2', colsClass[columns], className)}
    >
      {options.map((opt) => {
        const Icon = opt.icon
        const selected = value === opt.id
        const off = disabled || opt.disabled === true
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={off}
            onClick={() => onChange(opt.id)}
            className={cn(
              'flex items-center gap-2 p-2.5 rounded-2xl border-2 text-sm font-medium text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'border-foreground/40 bg-muted text-foreground'
                : 'border-border bg-card text-muted-foreground',
              // Ховер/нажатие только у живой карточки: :hover матчится и на disabled-кнопке,
              // иначе заблокированная карточка подсвечивалась бы как кликабельная.
              !selected && !off
                ? 'hover:bg-popover hover:border-foreground/20 hover:text-foreground active:bg-muted'
                : null,
            )}
          >
            {opt.swatch ? (
              <span
                data-swatch
                aria-hidden
                className="h-5 w-5 shrink-0 rounded border border-border"
                style={{ background: opt.swatch }}
              />
            ) : null}
            {Icon ? <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} /> : null}
            <span className="truncate">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
